// confirmation_code.ts - Handle 6-digit confirmation code input

import { on } from 'delegated-events'

function getCodeInputs(container: Element): NodeListOf<HTMLInputElement> {
  return container.querySelectorAll<HTMLInputElement>('.js-code-input')
}

function updateHiddenField(container: Element): string {
  const inputs = getCodeInputs(container)
  const form = container.closest<HTMLFormElement>('.js-code-form')
  const hiddenField = form?.querySelector<HTMLInputElement>('.js-code-hidden')

  const code = Array.from(inputs)
    .map((input) => input.value)
    .join('')

  if (hiddenField) {
    hiddenField.value = code
  }

  return code
}

function isComplete(container: Element): boolean {
  const inputs = getCodeInputs(container)
  return Array.from(inputs).every((input) => /^\d$/.test(input.value))
}

function submitForm(container: Element): void {
  const form = container.closest<HTMLFormElement>('.js-code-form')
  if (form) {
    form.submit()
  }
}

// Handle input
on('input', '.js-code-input', function (event: Event): void {
  const input = event.currentTarget as HTMLInputElement
  const container = input.closest('.js-code-container')
  if (!container) return

  const value = input.value

  // Only allow digits
  if (!/^\d?$/.test(value)) {
    input.value = ''
    return
  }

  updateHiddenField(container)

  // Move to next input
  const index = parseInt(input.dataset.index || '0', 10)
  const inputs = getCodeInputs(container)

  if (value && index < inputs.length - 1) {
    inputs[index + 1].focus()
  }

  // Auto-submit when complete
  if (isComplete(container)) {
    submitForm(container)
  }
})

// Handle backspace
on('keydown', '.js-code-input', function (event: Event): void {
  const keyboardEvent = event as KeyboardEvent
  const input = keyboardEvent.currentTarget as HTMLInputElement

  if (keyboardEvent.key !== 'Backspace' || input.value) return

  const container = input.closest('.js-code-container')
  if (!container) return

  const index = parseInt(input.dataset.index || '0', 10)
  if (index > 0) {
    const inputs = getCodeInputs(container)
    inputs[index - 1].focus()
  }
})

// Handle paste
on('paste', '.js-code-input', function (event: Event): void {
  event.preventDefault()

  const clipboardEvent = event as ClipboardEvent
  const input = clipboardEvent.currentTarget as HTMLInputElement
  const container = input.closest('.js-code-container')
  if (!container) return

  const paste = clipboardEvent.clipboardData?.getData('text') || ''
  const digits = paste.replace(/\D/g, '').slice(0, 6).split('')
  const inputs = getCodeInputs(container)

  digits.forEach((digit, i) => {
    if (inputs[i]) {
      inputs[i].value = digit
    }
  })

  updateHiddenField(container)

  // Focus appropriate input or auto-submit
  if (digits.length === 6) {
    submitForm(container)
  } else {
    const focusIndex = Math.min(digits.length, inputs.length - 1)
    inputs[focusIndex].focus()
  }
})
